import { TabPanel } from '@mui/lab'
import { Box, SelectChangeEvent, Typography, Grid } from '@mui/material'
import React, { useMemo, useState, useEffect } from 'react'
import EventCard from '../EventCard'
import { useAppSelector } from '../../../../redux/store'
import CustomTablePagination from '../../../Common/Table/CustomTablePagination'
import { EventWithAdditionalInfo } from '../../../../redux/event/teamMemberEvents/types'
import {
  selectTeamMemberEventError,
  selectTeamMemberEventLoading,
} from '../../../../redux/event/teamMemberEvents/selector'

interface EventTabPanelProps {
  value: string
  eventData: EventWithAdditionalInfo[]
}

interface RowsPerPageOption {
  id: string
  label: string
  value: number
}

const EventTabPanel: React.FC<EventTabPanelProps> = ({ value, eventData }) => {
  const loading = useAppSelector(selectTeamMemberEventLoading)
  const error = useAppSelector(selectTeamMemberEventError)
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(4)

  // Reset pagination when tab changes
  useEffect(() => {
    setRowsPerPage(4)
    setPage(0)
  }, [value])

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
    setPage(newPage)

  const handleChangeRowsPerPage = (event: SelectChangeEvent<unknown>) => {
    const newRowsPerPage = parseInt(event.target.value as string)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  const visibleData = useMemo<EventWithAdditionalInfo[]>(() => {
    const startIndex = page * rowsPerPage
    return rowsPerPage === -1 ? eventData : eventData.slice(startIndex, startIndex + rowsPerPage)
  }, [eventData, page, rowsPerPage])

  const rowsPerPageOptions: RowsPerPageOption[] = [
    {
      id: 'all-option',
      label: 'All',
      value: -1,
    },
    { id: 'four-per-page-option', label: '4', value: 4 },
    { id: 'eight-per-page-option', label: '8', value: 8 },
    { id: 'twenty-per-page-option', label: '20', value: 20 },
  ]

  return (
    <>
      <TabPanel value={value} sx={{ p: 0 }}>
        <Box sx={{ padding: 2 }}>
          {loading ? (
            <Box display='flex' justifyContent='center' alignItems='center' sx={{ height: 350 }}>
              <Typography>Loading ...</Typography>
            </Box>
          ) : error ? (
            <Box display='flex' justifyContent='center' alignItems='center' sx={{ height: 350 }}>
              <Typography color='error'>{`Error: ${error}`}</Typography>
            </Box>
          ) : visibleData.length === 0 ? (
            <Box display='flex' justifyContent='center' alignItems='center' sx={{ height: 350 }}>
              <Typography>No Data Found</Typography>
            </Box>
          ) : (
            <Grid container spacing={2} size={12}>
              {visibleData.map((event: EventWithAdditionalInfo) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={event.eventDetails.id}>
                  <EventCard eventData={event} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
        <Box>
          <CustomTablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            rowsPerPage={rowsPerPage}
            count={eventData.length}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </TabPanel>
    </>
  )
}

export default EventTabPanel
